import { useState } from "react";
import { Horse, Lock, Eye, EyeSlash, ArrowRight, ShieldCheck } from "@phosphor-icons/react";
import { loginUser, registerUser, createSubscriptionCheckout } from "../lib/api";
import { toast } from "sonner";

export default function AuthGate({ onAuth }) {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [agreed, setAgreed] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreed && mode === "register") {
      toast.error("Please agree to the Terms & Conditions");
      return;
    }
    setLoading(true);
    try {
      let user;
      if (mode === "login") {
        user = await loginUser(email, password);
      } else {
        user = await registerUser(email, password, name);
      }
      onAuth(user);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Authentication failed";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="auth-gate" className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/logo-40.png" alt="Racing Form Guide" className="w-14 h-14 mx-auto mb-3" />
          <h1 className="text-2xl font-black tracking-tight text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
            Racing Form Guide
          </h1>
          <p className="text-sm text-zinc-500 mt-1">AI-powered horse racing predictions</p>
        </div>

        {/* Pricing Banner */}
        <div className="border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-4 mb-6 text-center">
          <p className="text-[#D4AF37] font-mono text-xs uppercase tracking-widest mb-1">Monthly Subscription</p>
          <p className="text-white text-3xl font-black">£9.99<span className="text-sm text-zinc-400 font-normal">/month</span></p>
          <p className="text-zinc-500 text-xs mt-1">Full access to AI predictions, live streaming & daily tips</p>
        </div>

        {/* Auth Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === "register" && (
            <div>
              <label className="block text-xs font-mono text-zinc-400 uppercase tracking-wider mb-1">Full Name</label>
              <input
                data-testid="auth-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full bg-zinc-900 border border-zinc-700 text-white px-4 py-3 text-sm focus:border-[#E12B00] focus:outline-none transition-colors"
                placeholder="Your name"
              />
            </div>
          )}
          <div>
            <label className="block text-xs font-mono text-zinc-400 uppercase tracking-wider mb-1">Email</label>
            <input
              data-testid="auth-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full bg-zinc-900 border border-zinc-700 text-white px-4 py-3 text-sm focus:border-[#E12B00] focus:outline-none transition-colors"
              placeholder="you@example.com"
            />
          </div>
          <div>
            <label className="block text-xs font-mono text-zinc-400 uppercase tracking-wider mb-1">Password</label>
            <div className="relative">
              <input
                data-testid="auth-password"
                type={showPass ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="w-full bg-zinc-900 border border-zinc-700 text-white px-4 py-3 text-sm focus:border-[#E12B00] focus:outline-none transition-colors pr-10"
                placeholder="Min 6 characters"
              />
              <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white">
                {showPass ? <EyeSlash size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {mode === "register" && (
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                data-testid="auth-agree"
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-1 accent-[#E12B00]"
              />
              <span className="text-xs text-zinc-400">
                I agree to the{" "}
                <a href="/terms" target="_blank" className="text-[#D4AF37] underline">Terms of Service</a>,{" "}
                <a href="/privacy" target="_blank" className="text-[#D4AF37] underline">Privacy Policy</a>, and{" "}
                <a href="/disclaimer" target="_blank" className="text-[#D4AF37] underline">Disclaimer</a>.
                I understand the odds shown are calculated by RAW Company Tech and are not official bookmaker odds.
              </span>
            </label>
          )}

          <button
            data-testid="auth-submit"
            type="submit"
            disabled={loading}
            className="w-full bg-[#E12B00] text-white font-mono text-sm font-bold uppercase tracking-wider py-3.5 hover:bg-[#C52400] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {loading ? "Please wait..." : mode === "login" ? (
              <><Lock weight="bold" size={16} /> Sign In</>
            ) : (
              <><ArrowRight weight="bold" size={16} /> Create Account & Subscribe</>
            )}
          </button>
        </form>

        <div className="text-center mt-4">
          {mode === "login" ? (
            <p className="text-sm text-zinc-500">
              Don't have an account?{" "}
              <button data-testid="switch-to-register" onClick={() => setMode("register")} className="text-[#D4AF37] underline">Sign up for £9.99/mo</button>
            </p>
          ) : (
            <p className="text-sm text-zinc-500">
              Already have an account?{" "}
              <button data-testid="switch-to-login" onClick={() => setMode("login")} className="text-[#D4AF37] underline">Sign in</button>
            </p>
          )}
        </div>

        {/* Security Badge */}
        <div className="flex items-center justify-center gap-2 mt-6 text-zinc-600 text-[10px] font-mono">
          <ShieldCheck weight="bold" size={14} />
          <span>Secured by Stripe &middot; SSL Encrypted &middot; Cancel anytime</span>
        </div>

        {/* Legal Links */}
        <div className="flex items-center justify-center gap-4 mt-4 text-[10px] font-mono text-zinc-600">
          <a href="/terms" className="hover:text-zinc-400">Terms</a>
          <a href="/privacy" className="hover:text-zinc-400">Privacy</a>
          <a href="/disclaimer" className="hover:text-zinc-400">Disclaimer</a>
        </div>

        <div className="text-center mt-4 text-[9px] text-zinc-700 font-mono">
          &copy; {new Date().getFullYear()} RAW COMPANY TECH LIMITED &middot; Company No. 17349121
        </div>
      </div>
    </div>
  );
}
